import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { schemas } from '@ciam/form-kit';
import { useTranslation } from '@ciam/i18n';
import { useCaptchaStore } from '@features/login';
import type { UserInfo } from '@features/shared';
import { useLoginStore, useTenureCheckStore } from '@features/shared';
import { GigyaAPI } from '@ciam/gigya-api';
import { useNavigate } from 'react-router-dom';

const LOGIN_SCHEMA = (isShowCaptcha: boolean) =>
  z.object({
    loginId: schemas.email,
    password: schemas.password,
    ...(isShowCaptcha && { captchaToken: schemas.captchaToken }),
  });

type UseLoginFormType = z.infer<ReturnType<typeof LOGIN_SCHEMA>>;

const DEFAULT_VALUES = {
  loginId: '',
  password: '',
  captchaToken: '',
};

export const useLoginForm = () => {
  const { isShowCaptcha, showCaptcha, resetCaptcha } = useCaptchaStore();
  const { t } = useTranslation(['login']);
  const normalLogin = useLoginStore((state) => state.normalLogin);
  const checkTenureExpired = useTenureCheckStore((state) => state.checkTenureExpired);
  const navigate = useNavigate();

  const methods = useForm<UseLoginFormType>({
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(LOGIN_SCHEMA(isShowCaptcha)),
  });

  const { handleSubmit, setError, reset } = methods;

  const handleSetRootError = (message: string) => {
    return setError('root', {
      type: 'manual',
      message: message,
    });
  };

  const onSubmit = handleSubmit(async (formData) => {
    const { loginId, password, captchaToken } = formData as {
      loginId: string;
      password: string;
      captchaToken?: string;
    };

    // [STEP 1] 일반 로그인 요청
    const loginResult = await normalLogin({
      email: loginId,
      password: password,
      ...(captchaToken && { captchaToken }),
    });

    // [STEP 1-1] 로그인 실패 시
    if (!loginResult.success) {
      // 캡차 관련 처리
      if (loginResult.shouldShowCaptcha) showCaptcha();
      if (loginResult.shouldResetCaptcha) {
        resetCaptcha();
        reset({ ...formData, captchaToken: '' });
      }

      handleSetRootError(t(`errors.${loginResult.errorMessage}`));
    }
    // [STEP 1-2] 로그인 성공 시
    if (loginResult.success) {
      const { uid, uidSignature, signatureTimestamp } = loginResult.loginInfo as UserInfo;
      // [STEP 2] 재직 승인 여부 확인
      const checkTenureResult = await checkTenureExpired({ uid: uid });
      // [STEP 2-1] 재직 승인 api 요청 실패 시
      if (!checkTenureResult.success) {
        return handleSetRootError(t(`errors.tenure-check-failed`));
      }
      // [STEP 2-2] 재직 승인 필요할 시 리다이렉트
      if (checkTenureResult.redirectUrl) {
        navigate(checkTenureResult.redirectUrl);
        return { success: true };
      }
      // [STEP 3] 재직 승인 필요없을 시 로그인
      const data = await GigyaAPI.socialize.notifyLogin({
        dontHandleScreenSet: true,
        siteUID: uid,
        UIDSig: uidSignature,
        UIDTimestamp: signatureTimestamp,
      });

      if (data.errorCode !== 0) {
        return handleSetRootError(data.errorDetails as string);
      }
    }
  });

  return {
    ...methods,
    onSubmit,
    handleSetRootError,
  };
};
